import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { NAV } from "@/constants/testIds";

// 🔁 To switch logos: change "tree" to "wordmark"
const LOGO_VARIANT = "wordmark";

const links = [
  { id: "plans", label: "Plans", testid: NAV.plans },
  { id: "process", label: "How it works", testid: NAV.process },
  { id: "faq", label: "FAQ", testid: NAV.faq },
  { id: "quote", label: "Contact", testid: NAV.contact },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollOrLink = (id) => {
    if (location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 glass-header transition-all duration-300 ${
        scrolled ? "border-b border-border/50" : "border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="group">
          <Logo variant={LOGO_VARIANT} />
        </Link>

        <div className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollOrLink(l.id)}
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/about"
            data-testid={NAV.about}
            className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
          >
            About
          </Link>
        </div>

        <div className="hidden lg:block">
          <Button
            data-testid={NAV.ctaQuote}
            onClick={() => scrollOrLink("quote")}
            className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90"
          >
            Get a free quote
          </Button>
        </div>

        <button
          data-testid={NAV.mobileToggle}
          className="lg:hidden p-2"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden border-t border-border/50 bg-background/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollOrLink(l.id)}
                className="text-left text-base font-medium text-foreground/90 py-1"
              >
                {l.label}
              </button>
            ))}
            <Link to="/about" onClick={() => setMobileOpen(false)} className="text-left text-base font-medium py-1">
              About
            </Link>
            <Button onClick={() => scrollOrLink("quote")} className="rounded-full h-11 mt-2 bg-primary">
              Get a free quote
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
